import { ReactKeycloakProvider } from '@react-keycloak/web';
import { ConfigProvider } from 'antd';
import configProviderEn from 'antd/es/locale/en_US';
import configProviderJa from 'antd/es/locale/ja_JP';
import { Locale } from 'antd/lib/locale-provider';
import type Keycloak from 'keycloak-js';
import moment from 'moment';
import { FC } from 'react';
import { RawIntlProvider } from 'react-intl';
import { ThemeProvider } from 'styled-components';
import { createNewIntl } from './helpers/create-new-intl';
import { messagesEN } from './locale/en';
import { messagesJA } from './locale/ja';
import AppRouter from './routes';
import GlobalStyle from './styles/globalStyle';
import theme from './styles/theme';
import {
  getKeycloakInitConfig,
  getLanguage,
  setTokenToLocalStorage,
} from './utils/getKeycloakConfig';

interface RootElementProps {
  keycloakInstance: Keycloak;
}

const RootElement: FC<RootElementProps> = ({ keycloakInstance }) => {
  const currentLanguage = getLanguage();

  const Intl = createNewIntl(
    currentLanguage,
    currentLanguage === 'ja' ? messagesJA : messagesEN,
  );

  let configProviderLocale: Locale = configProviderEn;
  moment.locale(currentLanguage === 'ja' ? 'ja' : 'en');
  if (currentLanguage === 'ja') {
    configProviderLocale = configProviderJa;
  }

  const defaultFontFamily =
    currentLanguage === 'en'
      ? '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", "Open Sans", "Source Sans Pro", sans-serif'
      : '"Yu Gothic", "Meiryo", "MS Gothic", sans-serif';
  const fontFamily =
    currentLanguage === 'ja' ? 'Hiragino Kaku Gothic Pro' : 'Inter';

  return (
    <ReactKeycloakProvider
      authClient={keycloakInstance}
      onTokens={setTokenToLocalStorage}
      initOptions={getKeycloakInitConfig()}
    >
      <RawIntlProvider value={Intl}>
        <ConfigProvider locale={configProviderLocale}>
          <ThemeProvider
            theme={{
              ...theme,
              fontFamily,
              defaultFontFamily,
            }}
          >
            <GlobalStyle />
            <AppRouter />
          </ThemeProvider>
        </ConfigProvider>
      </RawIntlProvider>
    </ReactKeycloakProvider>
  );
};

export default RootElement;
